/**
 * User menu component for the header.
 *
 * Shows login button when unauthenticated, user dropdown when authenticated.
 */

import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";

export function UserMenu() {
  const { isLoading, isAuthenticated, user, isAdmin, login, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <button
        onClick={login}
        className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
      >
        Sign in
      </button>
    );
  }

  // Authenticated - show user menu
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full bg-gray-100 p-1 pr-3 hover:bg-gray-200 transition-colors"
      >
        {/* User avatar */}
        <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium">
          {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || "U"}
        </div>
        <span className="text-sm text-gray-700 hidden sm:block">
          {user?.displayName || user?.email}
        </span>
        {isAdmin && (
          <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded hidden sm:block">
            Admin
          </span>
        )}
        {/* Dropdown arrow */}
        <svg
          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {/* User info */}
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">
                {user?.displayName}
              </p>
              <p className="text-sm text-gray-500 truncate">{user?.email}</p>
              {user?.roles && user.roles.length > 0 && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {user.roles.map((role) => (
                    <span
                      key={role}
                      className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Menu items */}
            <a
              href="/profile"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Your Profile
            </a>
            <a
              href="/watchlist"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Watchlist
            </a>

            {isAdmin && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <a
                  href="/admin"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Admin Panel
                </a>
              </>
            )}

            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
